/**
 * useProductPreviewImage – resolve the best preview image for a product.
 *
 * Resolution order:
 * 1. media_assets cover (owner_type='product', kind='cover')
 * 2. media_assets gallery fallback (kind='gallery', first by sort_order)
 * 3. null
 *
 * All product images are resolved via media_assets (single source of truth).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset } from '@/lib/media/mediaTypes';

export type PreviewImageSource = 'media_assets' | 'none';

interface PreviewImageResult {
  imageUrl: string | null;
  isLoading: boolean;
  source: PreviewImageSource;
}

export function useProductPreviewImage(
  productId: string | null | undefined,
): PreviewImageResult {
  const { data, isLoading } = useQuery({
    queryKey: ['product-preview-image', productId],
    queryFn: async () => {
      if (!productId) return null;

      // Single query: fetch cover first, then gallery, limit to first match
      const { data: assets, error } = await supabase
        .from('media_assets')
        .select('id, path_320, path_640, path_1280, kind, sort_order, created_at, bucket, owner_id, owner_type, mime, width, height, size_bytes, alt_text, draft_session_id, season_key')
        .eq('owner_type', 'product')
        .eq('owner_id', productId)
        .in('kind', ['cover', 'gallery'])
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
        .limit(10); // small limit; we pick best in JS

      if (error) throw error;
      if (!assets || assets.length === 0) return null;

      // Prefer cover over gallery
      const cover = assets.find((a) => a.kind === 'cover');
      const best = cover ?? assets[0];

      const asset = best as unknown as MediaAsset;
      // Prefer 640, then 320, then 1280
      const url = resolveAssetUrl(asset, 640)
        ?? resolveAssetUrl(asset, 320)
        ?? resolveAssetUrl(asset, 1280);

      return url ?? null;
    },
    enabled: !!productId,
    staleTime: 30_000,
  });

  // Determine source
  if (data) {
    return { imageUrl: data, isLoading, source: 'media_assets' };
  }

  return { imageUrl: null, isLoading, source: 'none' };
}
